import { ProctoringOption } from "@verity/proctoring-option/types";
import { useContext } from "react";
import apiClient from "../../../../../helpers/api-client";
import verityLogger from "../../../../../helpers/logger";
import { intIdToUuid } from "../../../../../helpers/uuid";
import { PortalControllerContext } from "../../../../context";
import { DEFAULT_PLATFORM } from "../../constants";
import { ConferencePlatformType, InterviewDetailsType } from "../types";
import useUpdateLiveSessions from "./useUpdateLiveSessions";

//For standalone options
const dateFormatter = new Intl.DateTimeFormat("en-US", {
	year: "numeric",
	month: "long",
	day: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat("en-US", {
	hour: "numeric",
	minute: "2-digit",
	hour12: true,
	timeZone: "America/New_York",
	timeZoneName: "short",
});

function getAndFormatForAwsScheduler(date: Date) {
	const twoHoursBefore = date.getTime() - 2 * 60 * 60 * 1000;
	return twoHoursBefore > Date.now() ? new Date(twoHoursBefore).toISOString().replace(/\.\d{3}Z$/, "") : null;
}

function getFirstLastName(name: string) {
	const nameSplit = name.split(/ (.+)/);
	if (nameSplit.length === 2) return nameSplit;
	else return [...nameSplit, ""];
}

type EmailProps = {
	companyName: string;
	interviewers: string;
};

type UseSubmitHandlersProps = {
	candidateOptions: Omit<InterviewDetailsType & ConferencePlatformType, "startTime" | "endTime" | "interviewerUuids">;
	proctoringOptions: Partial<ProctoringOption>;
	sessionDates: {
		start: InterviewDetailsType["startTime"];
		end: InterviewDetailsType["endTime"];
	};
	interviewerUuids: InterviewDetailsType["interviewerUuids"];
	isEdit?: boolean;
};

export function useSubmitHandlers({
	candidateOptions,
	proctoringOptions,
	sessionDates,
	interviewerUuids,
	isEdit = false,
}: UseSubmitHandlersProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId, instituteUuid, courseUuid } = portalContext;

	function handleValidateDates() {
		if (!sessionDates.start || !sessionDates.end) throw new Error("Missing Date");
		if (new Date() > sessionDates.start) throw new Error("The start date is in the past");
		if (sessionDates.start >= sessionDates.end) throw new Error("The end date is before the start date");
	}

	async function handleSaveProctoringConfig(assessmentUuid: string) {
		if (!lmsProvidedCourseId) throw new Error("Missing course id");

		const [firstName, lastName] = getFirstLastName(candidateOptions.candidateName);

		const options = {
			assessmentInfo: {
				courseId: lmsProvidedCourseId,
				quizId: assessmentUuid,
				// lms: "candidate",
				quizInfo: {
					name: candidateOptions.title,
				},
			},
			proctoringAssessmentConfiguration: {
				isLiveProctored: true,
				isLivePopIn: false,
				enablePostReview: false,
				enabled: true,
				candidateInterviewOptions: {
					...candidateOptions,
					videoPlatform: candidateOptions.videoPlatform || DEFAULT_PLATFORM.value,
				},
			},
			proctoringOption: { ...proctoringOptions, isLockdown: true, isExtension: false, isChromeOs: false },
			...(!isEdit && {
				standaloneOptions: {
					studentInfoEntries: [
						{
							email: candidateOptions.candidateEmail,
							firstName: firstName,
							lastName: lastName,
						},
					],
					expiryDate: sessionDates.end || undefined,
					exemptedEmails: [],
					shouldSendInvites: true,
				},
			}),
		};

		void verityLogger?.assessment.edit.configurationModal.saveStarted({ ...options });
		const { standaloneStudentInfo: candidates } = await apiClient.proctoringConfiguration.save(options);

		if (!isEdit && (!candidates || candidates?.length !== 1)) throw new Error("We made the wrong number of users");

		if (!isEdit) return candidates![0].id;
	}

	const updateLiveSession = useUpdateLiveSessions();
	async function handleSaveLiveSession(assessmentUuid: string) {
		const session = {
			title: candidateOptions.title,
			start: sessionDates.start,
			end: sessionDates.end,
		};

		const sessionId = await updateLiveSession(session, assessmentUuid);
		return sessionId;
	}

	async function handleSaveAccessControlUser(
		assessmentId: number | undefined,
		candidateId: number,
		emailProps: EmailProps,
	) {
		if (!instituteUuid || !courseUuid || !assessmentId) throw new Error("Missing UUIDs");
		if (!sessionDates.start || !sessionDates.end) throw new Error("Missing Start Date");

		const [firstName, lastName] = getFirstLastName(candidateOptions.candidateName);

		const windowsDownloadUrl = (await apiClient.download.getCandidateAiDownloadUrl("win", "exe")).url;
		const macIntelDownloadUrl = (await apiClient.download.getCandidateAiDownloadUrl("mac")).url;
		const macSiliconDownloadUrl = (await apiClient.download.getCandidateAiDownloadUrl("mac-arm")).url;

		const { id: accountUuid, userIds } = await apiClient.user.createStandaloneUser({
			instituteUuid,
			courseUuid,
			assessmentUuid: intIdToUuid(assessmentId),
			email: candidateOptions.candidateEmail,
			firstName: candidateOptions.candidateName,
			lastName: "",
			expiryDate: sessionDates.end,
			emailTemplateData: {
				link: `${window.location.origin}/candidate/bridge?userId=${candidateId}&start-time=${sessionDates.start.toISOString()}&access-code=`,
				firstName: firstName,
				lastName: lastName,
				positionName: candidateOptions.positionName,
				reminderDate: getAndFormatForAwsScheduler(sessionDates.start),
				date: dateFormatter.format(sessionDates.start),
				time: timeFormatter.format(sessionDates.start),
				windowsDownloadUrl,
				macIntelDownloadUrl,
				macSiliconDownloadUrl,
				...emailProps,
			},
		});
		const userUuid = userIds[0];

		if (!assessmentId) throw new Error();

		await apiClient.assessment.linkStandaloneStudentUser({
			assessmentId: assessmentId,
			standaloneStudentId: candidateId,
			accountUuid,
			userUuid,
		});

		return userUuid;
	}

	async function handleRegisterUsers(sessionId: string, candidateUuid?: string) {
		if (candidateUuid)
			await apiClient.liveClient.saveLiveSessionInstituteUser({
				liveSessionId: sessionId,
				userId: candidateUuid,
				action: "register",
				role: "student",
				silent: true,
			});

		await Promise.all(
			interviewerUuids.map((uuid) =>
				apiClient.liveClient.saveLiveSessionInstituteUser({
					liveSessionId: sessionId,
					userId: uuid,
					action: "register",
					role: "proctor",
					silent: true,
				}),
			),
		);
	}

	return {
		handleValidateDates,
		handleSaveProctoringConfig,
		handleSaveLiveSession,
		handleSaveAccessControlUser,
		handleRegisterUsers,
	};
}
